import React from "react";
import { Control, FieldErrors, FieldValues } from "react-hook-form";
import { Variant } from "@mui/material/styles/createTypography";
import { AutocompleteInputChangeReason } from "@mui/material/Autocomplete";

/* =======================
   Text Field
======================= */
export interface IDaTextFieldProps<T extends FieldValues> {
  control: Control<T>;
  name: string;
  label: string;
  defaultValue?: string | number;
  readOnly?: boolean;
  required?: boolean;
  size?: "small" | "medium";
  variant?: "standard" | "filled" | "outlined";
  multiline?: boolean;
  rows?: number;
  errors: FieldErrors<T>;
}

/* =======================
   Date Time Picker
======================= */
export interface IDaDateTimePickerFieldProps<T extends FieldValues> {
  control: Control<T>;
  name: string;
  label: string;
  defaultValue?: string | number;
  readOnly?: boolean;
  required?: boolean;
  size?: "small" | "medium";
  variant?: "standard" | "filled" | "outlined";
  disableOpenPicker?: boolean;
  errors: FieldErrors<T>;
}

/* =======================
   Number Format
======================= */
export enum NumberFormatType {
  EIN = "##-#######",
  SSN = "###-##-####",
  US_Phone = "+1 (###)###-####",
  CR_ID = "###############",
  MAILER_ID = "#########",
}

export interface IDaNumberFormatFieldProps<T extends FieldValues>
  extends IDaTextFieldProps<T> {
  numberFormat?: NumberFormatType;
}

/* =======================
   Checkbox
======================= */
export interface IDaCheckboxFieldProps<T extends FieldValues> {
  control: Control<T>;
  name: string;
  label: string | number | React.ReactElement;
  disabled?: boolean;
  size?: "small" | "medium";
  variant?: "standard" | "filled" | "outlined";
  defaultChecked?: boolean;
  labelPlacement?: "top" | "bottom" | "start" | "end";
  errors: FieldErrors<T>;
}

/* =======================
   Autocomplete
======================= */
export interface IDaAutocompleteFieldProps<T extends FieldValues> {
  control: Control<T>;
  name: string;
  label: string;
  options?: Array<ISelectListItem>;
  onInputChange?: (
    event: React.SyntheticEvent,
    value: string,
    reason: AutocompleteInputChangeReason
  ) => void;
  defaultValue?: string | number;
  readOnly?: boolean;
  required?: boolean;
  size?: "small" | "medium";
  variant?: "standard" | "filled" | "outlined";
  noOptionsText?: string;
  errors: FieldErrors<T>;
}

/* =======================
   Select
======================= */
export interface IDaSelectFieldProps<T extends FieldValues> {
  control: Control<T>;
  name: string;
  label: string;
  selectListItems?: Array<ISelectListItem>;
  defaultValue?: string | number;
  required?: boolean;
  size?: "small" | "medium";
  variant?: "standard" | "filled" | "outlined";
  errors: FieldErrors<T>;
  showNone?: boolean | undefined;
}

/* =======================
   Hidden Field
======================= */
export interface IDaHiddenFieldProps<T extends FieldValues> {
  control: Control<T>;
  name: string;
  defaultValue?: string | number;
  errors: FieldErrors<T>;
}

/* =======================
   Misc
======================= */
export interface IFormTitleProps {
  title: string;
  titleIcon?: string;
  typeVariant?: Variant;
}

export interface ISectionHeaderProps {
  title: string;
  sectionIcon?: string;
  typeVariant?: Variant;
}

export interface ISelectListItem {
  value: string | number;
  label?: string;
}

export interface IShowMoreProps {
  text: string;
  showMoreText?: string;
  showLessText?: string;
  truncatedLength?: number;
  truncatedIndicator?: string;
  expanded?: boolean;
  typeVariant?: Variant;
}

export interface IHTMLCommentProps {
  text: string;
}
